var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { ChevronDown, Profile as ProfileIcon } from '@sumup/icons';
import { TrackingElement } from '@sumup/collector';
import styled from '../../../../styles/styled';
import { hideVisually, navigationItem } from '../../../../styles/style-mixins';
import Avatar from '../../../Avatar';
import Body from '../../../Body';
import Popover from '../../../Popover';
import { Skeleton } from '../../../Skeleton';
import { TRACKING_ELEMENTS } from '../../constants';
const profileWrapperStyles = ({ theme }) => css `
  height: 100%;
  padding: 0 ${theme.spacings.mega};
  border-left: ${theme.borderWidth.kilo} solid var(--cui-border-divider);
`;
const ProfileWrapper = styled.button(navigationItem, profileWrapperStyles);
const userAvatarStyles = ({ theme }) => css `
  width: ${theme.iconSizes.mega};
  height: ${theme.iconSizes.mega};

  ${theme.mq.mega} {
    width: ${theme.iconSizes.giga};
    height: ${theme.iconSizes.giga};
  }
`;
const UserAvatar = styled(Avatar)(userAvatarStyles);
const userDetailsStyles = ({ theme }) => css `
  ${theme.mq.untilMega} {
    ${hideVisually()};
  }

  ${theme.mq.mega} {
    margin: 0 ${theme.spacings.kilo};
    max-width: 20ch;
  }
`;
const UserDetails = styled.div(userDetailsStyles);
const truncateStyles = css `
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const chevronStyles = ({ theme }) => css `
  display: none;

  ${theme.mq.mega} {
    display: block;
    transition: transform ${theme.transitions.default};

    button[aria-expanded='true'] & {
      transform: rotate(180deg);
    }
  }
`;
const Chevron = styled(ChevronDown)(chevronStyles);
function Profile(_a) {
    var { user, label, isActive, isOpen } = _a, props = __rest(_a, ["user", "label", "isActive", "isOpen"]);
    return (_jsxs(ProfileWrapper, Object.assign({}, props, { type: "button", "aria-label": label, title: label, isActive: isOpen || isActive }, { children: [_jsx(Skeleton, Object.assign({ circle: true }, { children: user.avatar ? (_jsx(UserAvatar, Object.assign({}, user.avatar, { variant: "identity" }))) : (_jsx(ProfileIcon, { role: "presentation" })) })), _jsxs(UserDetails, { children: [_jsx(Skeleton, Object.assign({ css: truncateStyles }, { children: _jsx(Body, Object.assign({ size: "two", variant: "highlight" }, { children: user.name })) })), user.id && (_jsx(Skeleton, Object.assign({ css: truncateStyles }, { children: _jsx(Body, Object.assign({ size: "two" }, { children: user.id })) })))] }), _jsx(Chevron, { size: "16" })] })));
}
export function ProfileMenu({ user, label, actions, isActive, onToggle, trackingLabel, tracking, }) {
    const [isOpen, setOpen] = useState(false);
    const offset = { mainAxis: 8, crossAxis: -16 };
    useEffect(() => {
        if (onToggle) {
            onToggle(isOpen);
        }
    }, [onToggle, isOpen]);
    return (_jsx(TrackingElement, Object.assign({ name: TRACKING_ELEMENTS.PROFILE_SECTION, label: trackingLabel }, { children: _jsx(Popover, { isOpen: isOpen, onToggle: setOpen, component: (popoverProps) => (_jsx(Profile, Object.assign({}, popoverProps, { isOpen: isOpen, label: label, user: user, isActive: isActive }))), actions: actions, placement: "bottom-end", offset: offset, tracking: tracking ? Object.assign(Object.assign({}, tracking), { component: 'profile_menu' }) : undefined }) })));
}
